import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Coins, TrendingUp, Activity, RefreshCw, ShoppingCart, AlertTriangle, Loader2, Calendar, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { creditApi, userApi } from '@/services/api';
import { BuyCreditsModal } from './BuyCreditsModal';
import { PlanComparison } from './PlanComparison';
import { loadStripe } from '@stripe/stripe-js';

interface CreditsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreditsModal({ open, onOpenChange }: CreditsModalProps) {
  const [creditData, setCreditData] = useState<any>(null);
  const [usageData, setUsageData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [upgradingPlan, setUpgradingPlan] = useState(false);
  const [showDetailedComparison, setShowDetailedComparison] = useState(false);

  useEffect(() => {
    if (open) {
      loadCreditBalance();
      loadUsageData();
    }
  }, [open]);

  const loadCreditBalance = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await creditApi.getCreditBalance();
      if (response.success) {
        setCreditData(response);
        // Trigger credit update event so header also updates
        window.dispatchEvent(new Event('credits-updated'));
      } else {
        setError(response.error || 'Failed to load credit balance');
      }
    } catch (err: any) {
      console.error('Error loading credit balance:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadUsageData = async () => {
    try {
      const response = await userApi.fetchUsage();
      if (response) {
        setUsageData(response);
      }
    } catch (err: any) {
      console.error('Error loading usage data:', err);
      // Don't show error for usage data, just log it
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier?.toLowerCase()) {
      case 'pro':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'premium':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getTierDisplayName = (tier: string) => {
    switch (tier?.toLowerCase()) {
      case 'pro':
        return 'Pro';
      case 'premium':
        return 'Premium';
      default:
        return 'Free';
    }
  };

  const getCreditStatusColor = (remaining: number) => {
    if (remaining <= 5) return 'bg-red-100 text-red-800 border-red-200';
    if (remaining <= 10) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const handleUpgradePlan = async () => {
    try {
      setUpgradingPlan(true);
      const planType = subscriptionTier === 'free' ? 'pro' : 'premium';
      const response = await creditApi.createSubscriptionCheckout(planType);

      // Load Stripe and redirect to checkout
      let stripe = await loadStripe('pk_live_51HmQQxJLxKssJyu66slQWOnqolbwqzUeDxHabUfYXLylNdSUwDF3exVA4c7uN5EepIrwKPY599CDdPkZjIkIZ3JN00w35T2T3Q');
      if (import.meta.env.DEV) {
        stripe = await loadStripe('pk_test_51HmQQxJLxKssJyu6vtlQ7aN9uaEiEhLP6HJtDsbWXxYDbhpkvdgu02xGIuM4SVQ48qrGQVFK55ZujfFK4AGP1xG700XQVYcZq6');
      }

      if (stripe && response.session_id) {
        const { error } = await stripe.redirectToCheckout({ sessionId: response.session_id });
        if (error) {
          console.error('Stripe error:', error);
          alert('Failed to redirect to payment. Please try again.');
        }
      }
    } catch (error) {
      console.error('Upgrade plan failed:', error);
      alert('Failed to initiate upgrade. Please try again.');
    } finally {
      setUpgradingPlan(false);
    }
  };

  const remainingCredits = creditData?.remaining_credits ?? 0;
  const subscriptionTier = creditData?.subscription_tier ?? 'free';
  const isLowCredits = remainingCredits <= 10;
  const isVeryLowCredits = remainingCredits <= 5;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-yellow-600" />
              Credits & Usage
            </DialogTitle>
            <DialogDescription>
              View your current credit balance and subscription details
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-600 mx-auto mb-3 animate-spin" />
              <p className="text-gray-500">Loading credit information...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={loadCreditBalance}>Try Again</Button>
            </div>
          ) : (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="space-y-4">
                {/* Credit Balance Section */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <Coins className="w-5 h-5 text-yellow-600" />
                      Current Balance
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={loadCreditBalance}
                      className="gap-1"
                    >
                      <RefreshCw className="w-3 h-3" />
                      Refresh
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Credits Display */}
                    <div className={`p-4 rounded-lg border ${getCreditStatusColor(remainingCredits)}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-3xl font-bold">{remainingCredits}</div>
                        <Button
                          size="sm"
                          onClick={() => setShowBuyCredits(true)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <ShoppingCart className="w-3 h-3 mr-1" />
                          Buy More
                        </Button>
                      </div>
                      <div className="text-sm font-medium">Credits Remaining</div>
                    </div>

                    {/* Subscription Tier */}
                    <div className={`p-4 rounded-lg border ${getTierColor(subscriptionTier)}`}>
                      <div className="text-2xl font-bold mb-1">{getTierDisplayName(subscriptionTier)}</div>
                      <div className="text-sm font-medium">Subscription Tier</div>
                    </div>
                  </div>

                  {/* Low Credits Warning */}
                  {isLowCredits && (
                    <div className={`mt-4 p-4 rounded-lg border-l-4 flex items-start gap-3 ${
                      isVeryLowCredits
                        ? 'bg-red-50 border-red-400 text-red-800'
                        : 'bg-yellow-50 border-yellow-400 text-yellow-800'
                    }`}>
                      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        {isVeryLowCredits
                          ? `Very low credits! You have ${remainingCredits} credits remaining. Consider purchasing more credits to continue using AI features.`
                          : `Low credits warning: ${remainingCredits} credits remaining. You may want to consider purchasing more soon.`}
                      </div>
                    </div>
                  )}
                </div>

                {/* Usage Statistics */}
                {creditData?.usage_stats && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Activity className="w-5 h-5 text-blue-600" />
                      Usage Statistics
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Total Credits Used</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {creditData.usage_stats.total_tracked_credits_used || 0}
                        </span>
                      </div>
                      {creditData.usage_stats.analyses_count !== undefined && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Total Analyses</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {creditData.usage_stats.analyses_count}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Account Status */}
                {creditData?.account_status && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      Account Status
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Usage Percentage</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {creditData.account_status.usage_percentage?.toFixed(1)}%
                        </span>
                      </div>
                      {creditData.account_status.estimated_remaining_analyses !== undefined && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Estimated Remaining Analyses</span>
                          <span className="text-sm font-semibold text-gray-900">
                            ~{Math.ceil((creditData.account_status.estimated_remaining_analyses || 0) / 2.5)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Daily Onboarding Limits */}
                {usageData?.daily_onboarding && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      Daily Onboarding Limits
                    </h3>

                    <div className="text-sm text-gray-600 mb-4">
                      Current tier: <span className="font-semibold capitalize text-gray-900">
                        {usageData.subscription_tier}
                      </span> • Resets daily at midnight
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Patents & Publications Limit */}
                      <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-800">Patents & Publications</h4>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            usageData.daily_onboarding.patent_publications.percentage_used >= 80
                              ? 'bg-red-100 text-red-800'
                              : usageData.daily_onboarding.patent_publications.percentage_used >= 60
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {usageData.daily_onboarding.patent_publications.percentage_used}% used
                          </span>
                        </div>

                        <div className="mb-2">
                          <div className="flex justify-between text-sm mb-1">
                            <span>{usageData.daily_onboarding.patent_publications.current} / {usageData.daily_onboarding.patent_publications.limit}</span>
                            <span className="text-gray-600">
                              {usageData.daily_onboarding.patent_publications.remaining} remaining
                            </span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-gray-200">
                            <div
                              className={`h-2 rounded-full transition-all duration-300 ${
                                usageData.daily_onboarding.patent_publications.percentage_used >= 80
                                  ? 'bg-red-500'
                                  : usageData.daily_onboarding.patent_publications.percentage_used >= 60
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                              }`}
                              style={{
                                width: `${Math.min(100, usageData.daily_onboarding.patent_publications.percentage_used)}%`
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Document Uploads Limit */}
                      <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-800">Document Uploads</h4>
                          {usageData.daily_onboarding.uploads.limit === 0 ? (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                              Not available
                            </span>
                          ) : (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              usageData.daily_onboarding.uploads.percentage_used >= 80
                                ? 'bg-red-100 text-red-800'
                                : usageData.daily_onboarding.uploads.percentage_used >= 60
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {usageData.daily_onboarding.uploads.percentage_used}% used
                            </span>
                          )}
                        </div>

                        {usageData.daily_onboarding.uploads.limit === 0 ? (
                          <div className="text-center py-4">
                            <div className="text-sm text-gray-600">
                              Upgrade to Pro or Premium for document uploads
                            </div>
                          </div>
                        ) : (
                          <div className="mb-2">
                            <div className="flex justify-between text-sm mb-1">
                              <span>{usageData.daily_onboarding.uploads.current} / {usageData.daily_onboarding.uploads.limit}</span>
                              <span className="text-gray-600">
                                {usageData.daily_onboarding.uploads.remaining} remaining
                              </span>
                            </div>
                            <div className="w-full h-2 rounded-full bg-gray-200">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  usageData.daily_onboarding.uploads.percentage_used >= 80
                                    ? 'bg-red-500'
                                    : usageData.daily_onboarding.uploads.percentage_used >= 60
                                    ? 'bg-yellow-500'
                                    : 'bg-green-500'
                                }`}
                                style={{
                                  width: `${Math.min(100, usageData.daily_onboarding.uploads.percentage_used)}%`
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tier Comparison Table */}
                    {usageData.limits_by_tier && (
                      <div className="mt-4">
                        <h4 className="font-medium mb-2 text-gray-800">Plan Comparison</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="border-b border-gray-300 bg-gray-100">
                                <th className="text-left p-2 text-gray-700">Plan</th>
                                <th className="text-center p-2 text-gray-700">Patents/Publications</th>
                                <th className="text-center p-2 text-gray-700">Document Uploads</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(usageData.limits_by_tier).map(([tier, limits]: [string, any]) => (
                                <tr key={tier} className={`border-b ${
                                  tier.toLowerCase() === usageData.subscription_tier.toLowerCase()
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'border-gray-200'
                                }`}>
                                  <td className={`p-2 font-medium capitalize ${
                                    tier.toLowerCase() === usageData.subscription_tier.toLowerCase()
                                      ? 'text-blue-800'
                                      : 'text-gray-700'
                                  }`}>
                                    {tier}
                                    {tier.toLowerCase() === usageData.subscription_tier.toLowerCase() && (
                                      <span className="ml-2 text-xs px-2 py-1 rounded-full bg-blue-200 text-blue-800">
                                        Current
                                      </span>
                                    )}
                                  </td>
                                  <td className={`p-2 text-center ${
                                    tier.toLowerCase() === usageData.subscription_tier.toLowerCase()
                                      ? 'text-blue-800'
                                      : 'text-gray-600'
                                  }`}>
                                    {limits.patent_publications}/day
                                  </td>
                                  <td className={`p-2 text-center ${
                                    tier.toLowerCase() === usageData.subscription_tier.toLowerCase()
                                      ? 'text-blue-800'
                                      : 'text-gray-600'
                                  }`}>
                                    {limits.uploads === 0 ? 'None' : `${limits.uploads}/day`}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Buy Credits Section */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                    Need More Credits?
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Purchase additional credits to continue using AI-powered features. Credits never expire!
                  </p>
                  <Button
                    onClick={() => setShowBuyCredits(true)}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Buy Credits
                  </Button>
                </div>

                {/* Subscription Plans Comparison */}
                {subscriptionTier !== 'premium' && (
                  <div className="bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 rounded-lg border border-purple-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                      View Upgrades
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Compare plans and unlock more features with a subscription upgrade
                    </p>

                    {/* Plans Comparison Grid */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {/* Free Plan */}
                      <div className={`rounded-lg border-2 p-4 ${
                        subscriptionTier === 'free'
                          ? 'border-blue-400 bg-blue-50'
                          : 'border-gray-200 bg-white'
                      }`}>
                        <div className="text-center mb-3">
                          <h4 className="font-bold text-gray-900 mb-1">Free</h4>
                          <div className="text-2xl font-bold text-gray-900">$0</div>
                          <div className="text-xs text-gray-600">forever</div>
                        </div>
                        {subscriptionTier === 'free' && (
                          <div className="text-center mb-3 -mt-2">
                            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 font-medium">
                              Current Plan
                            </span>
                          </div>
                        )}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">All core workflow features</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">MPEP, CFR, USC access</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Office action analysis</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Standard support</span>
                          </div>
                        </div>
                      </div>

                      {/* Pro Plan */}
                      <div className={`rounded-lg border-2 p-4 ${
                        subscriptionTier === 'pro'
                          ? 'border-purple-400 bg-purple-50'
                          : 'border-purple-300 bg-white shadow-md'
                      }`}>
                        <div className="text-center mb-3">
                          <h4 className="font-bold text-gray-900 mb-1">Pro</h4>
                          <div className="text-2xl font-bold text-purple-700">$19</div>
                          <div className="text-xs text-gray-600">per month</div>
                        </div>
                        {subscriptionTier === 'pro' && (
                          <div className="text-center mb-3 -mt-2">
                            <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-800 font-medium">
                              Current Plan
                            </span>
                          </div>
                        )}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">100 signup credits ($10 value)</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">All Free features</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Full text search (MPEP/CFR)</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">File upload for tasks & chat</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Foreign patents access</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Priority support</span>
                          </div>
                        </div>
                        {subscriptionTier === 'free' && (
                          <Button
                            onClick={() => handleUpgradePlan()}
                            disabled={upgradingPlan}
                            className="w-full mt-4 bg-purple-600 hover:bg-purple-700"
                            size="sm"
                          >
                            {upgradingPlan ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <TrendingUp className="w-3 h-3 mr-1" />
                            )}
                            Get Pro
                          </Button>
                        )}
                      </div>

                      {/* Premium Plan */}
                      <div className={`rounded-lg border-2 p-4 ${
                        subscriptionTier === 'premium'
                          ? 'border-pink-400 bg-pink-50'
                          : 'border-pink-300 bg-white shadow-lg'
                      }`}>
                        <div className="text-center mb-3">
                          <h4 className="font-bold text-gray-900 mb-1">Premium</h4>
                          <div className="text-2xl font-bold text-pink-700">$47</div>
                          <div className="text-xs text-gray-600">per month</div>
                        </div>
                        {subscriptionTier === 'premium' && (
                          <div className="text-center mb-3 -mt-2">
                            <span className="text-xs px-2 py-1 rounded-full bg-pink-100 text-pink-800 font-medium">
                              Current Plan
                            </span>
                          </div>
                        )}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">1,000 signup credits ($100 value)</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">All Pro features</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Saved drafts library</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">Translation & PDF OCR</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">LaTeX conversion & batch processing</span>
                          </div>
                        </div>
                        {subscriptionTier !== 'premium' && (
                          <Button
                            onClick={() => handleUpgradePlan()}
                            disabled={upgradingPlan}
                            className="w-full mt-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 whitespace-normal h-auto py-2"
                            size="sm"
                          >
                            {upgradingPlan ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 flex-shrink-0 animate-spin" />
                                <span>Processing...</span>
                              </>
                            ) : (
                              <>
                                <TrendingUp className="w-3 h-3 mr-1 flex-shrink-0" />
                                <span>Get Premium</span>
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="text-xs text-gray-600 text-center">
                      All plans include unlimited access to onboarded documents and core features
                    </div>

                    {/* Detailed Comparison Toggle */}
                    <div className="mt-6 text-center">
                      <Button
                        variant="outline"
                        onClick={() => setShowDetailedComparison(!showDetailedComparison)}
                        className="text-sm"
                      >
                        {showDetailedComparison ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-2" />
                            Hide Detailed Comparison
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-2" />
                            View Detailed Feature Comparison
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Detailed Plan Comparison */}
                    {showDetailedComparison && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <PlanComparison
                          currentPlan={subscriptionTier.toLowerCase() as 'free' | 'pro' | 'premium'}
                          onUpgrade={handleUpgradePlan}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        open={showBuyCredits}
        onOpenChange={setShowBuyCredits}
      />
    </>
  );
}
